import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Report from 'webapp/models-custom/report';

export const viewSettingsNamePrefix = 'popupSelectReportsViewSettings';
export const filterSettingsNamePrefix = 'popupSelectReportsFilterSettings';

export default class FilterSortReportAtSelectPopup extends FilterSort<Report> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		name: 1,
	};

	// Use this only to save selected filters in modal until its destroyed (resetFilterSettings used in boards/popup-select to reset filters)
	// Case: Use selected filters when switching between views grid/table then reset it for new modal "session".
	override settingsNamePrefix = filterSettingsNamePrefix;

	constructor(options: FilterSortSettings<Report>) {
		super(options);
		const selectedFilters = this.readSavedSelectedFilters();

		// Tags (used only)
		this.createFilter<number>({
			id: 1,
			selected: selectedFilters[1] as number | undefined,
			label: 'Tag',
			name: 'tag',
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => item.tags.includes(selectedValue));
			},
		});

		// Creator
		this.createFilter<number>({
			id: 2,
			selected: selectedFilters[2] as number | undefined,
			label: 'Created by',
			name: 'created by',
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some(
					(selectedValue) => (item?.ownerId || item?.createdBy) === selectedValue,
				);
			},
		});
	}

	override get viewOptions(): FilterOption<string>[] {
		return [
			{ id: 'table', text: 'List view', icon: 'List' },
			{ id: 'grid', text: 'Grid view', icon: 'Grid' },
		];
	}

	get tagOptions(): FilterOption<number>[] {
		const formattedOptions = [...this.mmReportsService.assignedTags] as FilterOption<number>[];

		if (formattedOptions.length) {
			formattedOptions.unshift({ id: 0, text: 'All' });
		}

		return formattedOptions;
	}

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdBy;
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}
}
